import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
const FAQ_DATA = [{
  question: 'What is the difference between pressure washing and power washing?',
  answer: 'Pressure washing and power washing are similar, but there is a key difference. Both use high-pressure water to clean surfaces, but power washing uses heated water. The heat makes power washing more effective for tough stains, such as grease, oil, and grime. In Phoenix, due to the high temperatures, power washing can be particularly beneficial for removing embedded dirt and grime from surfaces like driveways, sidewalks, and patios.'
}, {
  question: 'What surfaces can be cleaned with pressure/power washing?',
  answer: 'Pressure washing can be used on a variety of surfaces including driveways, sidewalks, patios, decks, fences, exterior walls, roofs, and even some outdoor furniture. In Phoenix, where dust and dirt accumulate quickly due to the dry climate, pressure washing is especially useful for concrete, brick, stucco, and stone surfaces. However, more delicate surfaces, such as wood or painted exteriors, require lower pressure or specialized techniques.'
}, {
  question: 'How often should I have my property pressure washed in Phoenix?',
  answer: "Due to Phoenix's desert climate, dust and dirt can accumulate quickly, especially during dust storms (haboobs). It is generally recommended to pressure wash your property at least once or twice a year. High-traffic areas like driveways and walkways may need more frequent cleaning. If you are preparing to sell your home, seasonal maintenance, or post-monsoon cleanup, power washing can be highly beneficial."
}, {
  question: 'Can pressure washing damage my property?',
  answer: "When done correctly, pressure washing is safe for most surfaces. However, if too much pressure is applied or incorrect techniques are used, it can cause damage to softer materials such as wood, vinyl siding, or painted surfaces. In Phoenix, where stucco and adobe homes are common, it's important to use the appropriate pressure level to avoid chipping or damaging the exterior."
}, {
  question: 'Is pressure washing environmentally friendly?',
  answer: "Pressure washing is generally environmentally friendly, as it relies mostly on water and pressure rather than harmful chemicals. Many pressure washing companies in Phoenix use eco-friendly, biodegradable cleaning solutions when needed. Additionally, Phoenix-based businesses often comply with local water regulations, especially considering Arizona's focus on water conservation."
}, {
  question: 'How long does a pressure washing service take?',
  answer: 'The duration of a pressure washing service depends on the size and condition of the area being cleaned. For an average home exterior or driveway in Phoenix, it typically takes 1 to 3 hours. Larger properties or heavily stained surfaces may take longer. Scheduling during cooler times of day, such as early morning or late afternoon, can help avoid the intense heat.'
}, {
  question: 'What are the benefits of pressure washing my home or business?',
  answer: 'Pressure washing helps remove dirt, grime, mold, mildew, and stains, improving the overall appearance of your property. In Phoenix, it can help mitigate the effects of dust accumulation and sun damage, prolonging the life of surfaces. For businesses, a clean exterior creates a positive first impression. Regular pressure washing can also reduce allergens and make outdoor areas safer by removing slippery substances like algae or oil.'
}, {
  question: 'Do I need to be home during the pressure washing service?',
  answer: "You do not need to be home for a pressure washing service as long as the team has access to the areas needing cleaning and a water source. However, it is important to ensure that all windows are closed, and outdoor items like furniture and potted plants are moved away from the area. In Phoenix, it's also a good idea to schedule the service for a cooler part of the day to avoid excessive evaporation or water drying too quickly."
}, {
  question: 'How much does pressure washing cost in Phoenix?',
  answer: 'Pricing for pressure washing services in Phoenix depends on the size of the area, the type of surface, and the level of dirt or staining. For a standard driveway, costs typically range from $100 to $200. For full house exteriors, prices may range from $250 to $500 or more. Phoenix-based companies may also offer discounts for bundling multiple services or for regular maintenance plans.'
}, {
  question: 'What should I do to prepare for a pressure washing service?',
  answer: 'To prepare for a pressure washing service, remove any fragile items, outdoor furniture, and decorations from the area. Ensure that all windows and doors are securely closed to prevent water from entering your home. If you live in a desert landscape area common in Phoenix, cover sensitive plants or landscaping features to avoid any water damage. Let your pressure washing technician know if you have specific areas of concern or any fragile surfaces that need special attention.'
}];
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b transition-colors" style={{
    borderColor: 'rgba(2,131,199,0.12)'
  }} data-id="element-118">
      <button className="w-full flex items-center justify-between py-5 px-1 text-left transition-opacity hover:opacity-80" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} data-id="element-119">
        <span className="font-bold pr-4" style={{
        color: '#0283C7',
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: 1.4
      }} data-id="element-120">
          {question}
        </span>
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all" style={{
        backgroundColor: isOpen ? '#0283C7' : '#E8F4FD'
      }} data-id="element-121">
          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{
          color: isOpen ? '#ffffff' : '#0283C7'
        }} data-id="element-122" />
        </div>
      </button>
      <div className="overflow-hidden transition-all duration-300" style={{
      maxHeight: isOpen ? '500px' : '0px',
      opacity: isOpen ? 1 : 0
    }} data-id="element-123">
        <p className="pb-5 px-1" style={{
        color: '#025A8A',
        fontFamily: 'Inter, sans-serif',
        fontSize: '15px',
        lineHeight: 1.8
      }} data-id="element-124">
          {answer}
        </p>
      </div>
    </div>;
}
export function FAQSection() {
  return <section className="w-full py-20 px-4" style={{
    backgroundColor: '#ffffff'
  }} data-id="element-125">
      <div className="max-w-4xl mx-auto" data-id="element-126">
        <div className="text-center mb-14" data-id="element-127">
          <p className="uppercase tracking-[0.2em] mb-3 text-sm" style={{
          color: '#0283C7',
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700
        }} data-id="element-128">
            FAQ
          </p>
          <h2 className="font-bold mb-4" style={{
          color: '#0283C7',
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 900,
          fontSize: 'clamp(24px, 4vw, 36px)',
          lineHeight: 1.2
        }} data-id="element-129">
            Frequently Asked Questions
          </h2>
          <p className="max-w-2xl mx-auto" style={{
          color: '#025A8A',
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          lineHeight: 1.7
        }} data-id="element-130">
            Everything you need to know about our power washing services in the
            Phoenix metro area.
          </p>
        </div>

        <div className="rounded-2xl p-6 md:p-10" style={{
        backgroundColor: '#F8FBFE',
        boxShadow: '0 4px 24px rgba(2,131,199,0.06)'
      }} data-id="element-131">
          {FAQ_DATA.map((faq, i) => <FAQItem key={i} question={faq.question} answer={faq.answer} data-id="element-132" />)}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center" data-id="element-133">
          <p className="mb-4" style={{
          color: '#025A8A',
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px'
        }} data-id="element-134">
            Still have questions? We're here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-135">
            <a href="/contact" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-white text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
            backgroundColor: '#0283C7',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700
          }} data-id="element-136">
              Contact Us
            </a>
            <a href="tel:4805998442" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-base transition-all hover:bg-blue-50" style={{
            backgroundColor: 'transparent',
            color: '#0283C7',
            border: '2px solid #0283C7',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700
          }} data-id="element-137">
              Call (480) 599-8442
            </a>
          </div>
        </div>
      </div>
    </section>;
}